import { type CompareResult, previousPeriod, type RetentionResult, type RevenueSummary, todayIn, type UaSummary } from '@growth/client';
import { Link, useParams } from 'react-router';
import { BarList, ColumnChart, LineChart } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader, useGames } from '../components/Layout.tsx';
import { TargetBadge } from '../components/money.tsx';
import { ChartCard, ErrorBox, SampleBadge, StatTile } from '../components/ui.tsx';
import { describeFilters, useGlobalFilters } from '../filters.ts';
import { fmtAgo, fmtCompact, fmtDay, fmtDuration, fmtInt, fmtMoney, fmtNum, fmtPct } from '../format.ts';
import { ciText, deltaOf, funnelItems, funnelStages, MIN_SAMPLE, retentionSeries } from '../metrics.ts';
import { useApi } from '../session.tsx';

export function useGameTimezone(gameId: string): string | null {
  const games = useGames();
  if (!games.data) return null;
  return games.data.find((g) => g.id === gameId)?.reporting_timezone ?? 'UTC';
}

export function FreshnessLine({ fresh }: { fresh: CompareResult['b']['fresh'] | undefined }) {
  if (!fresh) return null;
  const pending = fresh.pending_sessions + fresh.pending_runs + fresh.pending_activity;
  return (
    <span>
      Último evento {fmtAgo(fresh.last_event_received_at)} · métricas derivadas {fmtAgo(fresh.last_derive_at)}
      {pending > 0 ? ` · ${fmtInt(pending)} pendientes de derivar` : ''}
    </span>
  );
}

export function OverviewPage() {
  const { gameId = '' } = useParams();
  const filters = useGlobalFilters();
  const tz = useGameTimezone(gameId);
  const today = tz ? todayIn(tz) : null;
  const prev = today ? previousPeriod(filters.params, today) : null;

  // A = previous period, B = selected period: deltas are B − A ("vs periodo anterior").
  const cmp = useApi<CompareResult>(prev ? `cmp:${gameId}:${filters.key}` : null, (api, s) => api.compare(gameId, prev!, filters.params, s));
  const ret = useApi<RetentionResult>(`ret:${gameId}:${filters.key}:none`, (api, s) => api.retention(gameId, { ...filters.params, group_by: 'none' }, s));
  const ua = useApi<UaSummary>(`ua-summary:${gameId}`, (api, s) => api.uaSummary(gameId, s));
  const revenue = useApi<RevenueSummary>(`rev-summary:${gameId}`, (api, s) => api.revenueSummary(gameId, s));
  const rv = revenue.data;
  const u = ua.data;
  const hasUa = !!u && (u.coverage.connections.length > 0 || u.spend_7d !== null);
  const cur = cmp.data?.b;
  const d = cmp.data?.deltas;
  const e = cur?.engagement;
  const act = cur?.activation;
  const r1 = cur?.retention?.days.find((x) => x.day === 1);
  const r7 = cur?.retention?.days.find((x) => x.day === 7);
  const prevRet = cmp.data?.a.retention;
  const okSample = (day: number, n: number | undefined) => (n ?? 0) >= MIN_SAMPLE && (prevRet?.days.find((x) => x.day === day)?.mature ?? 0) >= MIN_SAMPLE;
  const vs = 'periodo anterior';
  const group = ret.data?.groups[0];
  const series = e?.series ?? [];

  return (
    <>
      <PageHeader title="Resumen" sub={<FreshnessLine fresh={cur?.fresh} />} />
      <FilterBar filters={filters} />
      {cmp.error && <ErrorBox error={cmp.error} onRetry={cmp.reload} />}

      <div className={`kpis loading-frame${cmp.loading && cmp.data ? ' stale' : ''}`}>
        <StatTile
          label="DAU medio"
          value={fmtCompact(e?.dau_avg)}
          delta={deltaOf(d?.dau_avg, { vs })}
          sub={e ? `WAU ${fmtCompact(e.wau)} · MAU ${fmtCompact(e.mau)}` : undefined}
          trend={series.map((p) => p.dau)}
        />
        <StatTile label="Instalaciones" value={fmtCompact(e?.new_users)} delta={deltaOf(d?.new_users, { vs })} sub="nuevos usuarios" trend={series.map((p) => p.new_users)} />
        <StatTile label="Retención D1" value={fmtPct(r1?.classic.rate)} delta={okSample(1, r1?.mature) ? deltaOf(d?.d1, { vs, rate: true }) : undefined} sub={ciText(r1?.classic)} />
        <StatTile label="Retención D7" value={fmtPct(r7?.classic.rate)} delta={okSample(7, r7?.mature) ? deltaOf(d?.d7, { vs, rate: true }) : undefined} sub={ciText(r7?.classic)} />
        <StatTile label="Sesiones por DAU" value={fmtNum(e?.sessions_per_dau, 2)} delta={deltaOf(d?.sessions_per_dau, { vs })} sub={e ? `${fmtCompact(e.sessions)} sesiones` : undefined} />
        <StatTile label="Duración de sesión" value={fmtDuration(e?.avg_session_s)} delta={deltaOf(d?.avg_session_s, { vs })} sub={e ? `mediana ${fmtDuration(e.median_session_s)}` : undefined} />
        <StatTile label="Tiempo de juego por DAU" value={fmtDuration(e?.playtime_per_dau_s)} delta={deltaOf(d?.playtime_per_dau_s, { vs })} />
        <StatTile label="Runs por jugador" value={fmtNum(e?.runs_per_player, 2)} delta={deltaOf(d?.runs_per_player, { vs })} sub={e ? `${fmtCompact(e.runs_started)} runs · ${fmtCompact(e.runs_completed)} terminadas` : undefined} />
        {hasUa ? (
          <>
            <StatTile label="Gasto UA hoy" value={fmtMoney(u.spend_today, u.currency)} sub={`7 días: ${fmtMoney(u.spend_7d, u.currency)}`} />
            <StatTile label="Instalaciones (red) 7 días" value={fmtCompact(u.installs_7d)} sub={`CPI ${fmtMoney(u.cpi_7d, u.currency, 3)} · CTR ${fmtPct(u.ctr_7d, 2)}`} />
            <StatTile
              label="Mejor creatividad"
              value={u.top_creative ? u.top_creative.name : '—'}
              compact
              sub={
                u.top_creative ? (
                  <Link to={`/g/${gameId}/creatives/${encodeURIComponent(u.top_creative.creative_id)}`}>
                    {u.top_creative.requires_manual_approval ? 'candidata a escalar (requiere tu aprobación)' : `mejor CPI: ${fmtMoney(u.top_creative.value, u.currency, 3)}`}
                  </Link>
                ) : (
                  'sin muestra suficiente todavía'
                )
              }
            />
            <StatTile
              label="Creatividades a revisar"
              value={fmtInt(u.creatives_needing_attention.length)}
              sub={
                u.creatives_needing_attention.length ? (
                  <Link to={`/g/${gameId}/creatives`}>{u.creatives_needing_attention.map((c) => c.name).join(', ')}</Link>
                ) : (
                  'sin fatiga ni CPI por encima del objetivo'
                )
              }
            />
          </>
        ) : (
          <StatTile label="Gasto UA" value="—" sub={<Link to={`/g/${gameId}/settings/connections`}>Conecta Google Ads</Link>} placeholder />
        )}
        <StatTile
          label="Ingresos 7 días"
          value={fmtMoney(rv?.revenue_7d, rv?.currency ?? 'EUR')}
          sub={rv ? <Link to={`/g/${gameId}/monetization${filters.search}`}>anuncios {fmtMoney(rv.ad_revenue_7d, rv.currency)} · IAP {fmtMoney(rv.iap_revenue_7d, rv.currency)} (neto est.)</Link> : undefined}
        />
        <StatTile label="ARPDAU 7 días" value={fmtMoney(rv?.arpdau_7d, rv?.currency ?? 'EUR', 3)} sub={rv ? `hoy ${fmtMoney(rv.revenue_today, rv.currency)}` : undefined} />
        <StatTile
          label="LTV D7"
          value={fmtMoney(rv?.ltv_d7.value, rv?.currency ?? 'EUR', 3)}
          sub={rv ? <Link to={`/g/${gameId}/ltv${filters.search}`}>{fmtInt(rv.ltv_d7.mature_installs)} jugadores con 7 días (cohortes de 30 días)</Link> : undefined}
        />
        <StatTile
          label="ROAS D7"
          value={fmtPct(rv?.roas_d7.value, 0)}
          sub={
            rv ? (
              <>
                {rv.roas_d7.value !== null && <TargetBadge status={rv.roas_d7.status} />} {rv.roas_d7.spend ? `${rv.roas_d7.attribution === 'shared' ? 'atribución compartida' : 'mezclada'}` : 'necesita gasto en redes'}
              </>
            ) : undefined
          }
        />
      </div>

      <div className="grid cols-2">
        <ChartCard
          title="Usuarios activos por día"
          subtitle={`Nuevos y recurrentes${describeFilters(filters.params) ? ` · ${describeFilters(filters.params)}` : ''}`}
          stale={cmp.loading && !!cmp.data}
          table={{
            columns: [{ label: 'Día' }, { label: 'DAU', numeric: true }, { label: 'Nuevos', numeric: true }, { label: 'Recurrentes', numeric: true }, { label: 'Sesiones', numeric: true }],
            rows: series.map((p) => [p.date, fmtInt(p.dau), fmtInt(p.new_users), fmtInt(p.returning_users), fmtInt(p.sessions)]),
          }}
        >
          <ColumnChart
            ariaLabel="Usuarios activos por día"
            x={series.map((p) => p.date)}
            formatX={(v) => fmtDay(String(v))}
            formatY={fmtCompact}
            formatValue={fmtInt}
            totalLabel="DAU"
            series={[
              { key: 'returning', label: 'Recurrentes', slot: 1, values: series.map((p) => p.returning_users) },
              { key: 'new', label: 'Nuevos', slot: 2, values: series.map((p) => p.new_users) },
            ]}
          />
        </ChartCard>

        <ChartCard
          title="Curva de retención"
          subtitle={
            group ? (
              <>
                Clásica, cohortes instaladas en el periodo · banda = IC 95 % · <SampleBadge sample={group.sample} n={group.cohort_size} />
              </>
            ) : (
              'Sin cohortes en el periodo'
            )
          }
          stale={ret.loading && !!ret.data}
          table={
            group && {
              columns: [{ label: 'Día' }, { label: 'Retención', numeric: true }, { label: 'IC 95 %', numeric: true }, { label: 'Maduros', numeric: true }, { label: 'Madurez', numeric: true }],
              rows: group.days.map((c) => [`D${c.day}`, fmtPct(c.classic.rate), c.classic.rate === null ? '—' : `${fmtPct(c.classic.ci_low)}–${fmtPct(c.classic.ci_high)}`, fmtInt(c.mature), fmtPct(c.maturity, 0)]),
            }
          }
        >
          {group ? (
            <LineChart
              ariaLabel="Curva de retención clásica"
              x={group.days.map((c) => c.day)}
              linearX
              formatX={(v) => `D${v}`}
              formatY={(v) => fmtPct(v, 0)}
              formatValue={(v) => fmtPct(v)}
              yCap={1}
              series={[retentionSeries(group, 'classic', 1, 'Retención')]}
              tooltipNote={(i) => {
                const c = group.days[i];
                return c ? `${fmtInt(c.mature)} instalaciones maduras (${fmtPct(c.maturity, 0)} de la cohorte)` : undefined;
              }}
            />
          ) : (
            <div className="empty">Sin instalaciones nuevas en el periodo.</div>
          )}
        </ChartCard>
      </div>

      <div className="grid cols-2">
        <ChartCard
          title="Activación"
          subtitle={act ? <>Embudo de la cohorte del periodo · <SampleBadge sample={act.sample} n={act.installs} /></> : undefined}
          stale={cmp.loading && !!cmp.data}
          table={
            act && {
              columns: [{ label: 'Etapa' }, { label: 'Jugadores', numeric: true }, { label: '% instalaciones', numeric: true }, { label: 'IC 95 %', numeric: true }],
              rows: funnelStages(act).map((s) => [s.label, fmtInt(s.count), s.rate ? fmtPct(s.rate.rate) : '100 %', s.rate ? `${fmtPct(s.rate.ci_low)}–${fmtPct(s.rate.ci_high)}` : '—']),
            }
          }
        >
          {act && <BarList ariaLabel="Embudo de activación" items={funnelItems(act)} format={fmtInt} />}
        </ChartCard>
        <ChartCard title="Primeras horas del jugador" subtitle="Cohorte del periodo (nuevas instalaciones)">
          {act && (
            <dl className="kv">
              <dt>Tutorial iniciado</dt>
              <dd>
                {fmtPct(act.tutorial_start_rate.rate)} <span className="muted small">{ciText(act.tutorial_start_rate)}</span>
              </dd>
              <dt>Completan el tutorial (de quienes lo empiezan)</dt>
              <dd>{fmtPct(act.tutorial_completion_of_starters.rate)}</dd>
              <dt>Ven un rewarded</dt>
              <dd>
                {fmtPct(act.rewarded_ad_user_rate.rate)} · runs hasta el primero: media {fmtNum(act.avg_runs_to_first_rewarded_ad, 1)}, mediana {fmtNum(act.median_runs_to_first_rewarded_ad, 1)}
              </dd>
              <dt>Compran</dt>
              <dd>
                {fmtPct(act.payer_rate.rate)} · runs hasta la primera compra: media {fmtNum(act.avg_runs_to_first_purchase, 1)}
              </dd>
              <dt>Abandonan (sin actividad {act.churn.churn_days} días)</dt>
              <dd>
                {fmtInt(act.churn.churned)} · runs jugadas antes de irse: media {fmtNum(act.churn.avg_runs_until_churn, 1)}, mediana {fmtNum(act.churn.median_runs_until_churn, 1)}
              </dd>
            </dl>
          )}
        </ChartCard>
      </div>
    </>
  );
}
